import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ExpandableSection, SpaceBetween, Button, FormField, Select, DatePicker, Box, Spinner, Modal, Alert } from '@amzn/awsui-components-core';
import List from '@amzn/awsui-components-core/list';
import { v4 as uuidv4 } from 'uuid';
import { TimeSlot, ResolvedMatch } from '../../interfaces/interface';
import { SchedulingActions } from '../../hooks/useScheduling';
import { TIMESLOT_SUMMARY_PROMPT } from '../../constants/EAHuddleConstants';
import { EmployeeSettingsService } from '../../services/EmployeeSettingsService';
import { TimeSlotCard } from './TimeSlotCard';
import { getUserTimezone, getNextStartTime, getMinStartTime, getMinEndTime } from 'src/utilities/DateTimeUtilities';
import { DateTime, IANAZone } from 'luxon';
import { logger } from 'src/logger';
import { BedrockService } from '../../services/BedrockService';
import { MeetingService } from '../../services/MeetingService';
import { TimeSlotSummaryModal } from './modals/TimeSlotSummaryModal';
import { CreateMeetingModal } from './modals/CreateMeetingModal';
import './SchedulingContainer.scss';

// ── Helper: format a time slot for the dropdown ────────────────────────

function formatTimeSlotLabel(slot: TimeSlot): string {
  const userTz = getUserTimezone();
  const effectiveTz = slot.timezone || userTz;
  const start = DateTime.fromFormat(`${slot.date} ${slot.startTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz }).setZone(userTz);
  const end = DateTime.fromFormat(`${slot.date} ${slot.endTime}`, 'yyyy-MM-dd HH:mm', { zone: effectiveTz }).setZone(userTz);
  const abbr = start.toFormat('ZZZZ');
  return `${start.toFormat('EEEE, MMMM d, yyyy')} · ${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')} ${abbr}`;
}

// ── Props ──────────────────────────────────────────────────────────────

interface SchedulingContainerProps {
  organizerLogin: string;
  currentUserAlias: string;
  currentUserName: string;
  scheduling: SchedulingActions;
  onEnsureMeetingCreated: () => Promise<string | null>;
  isReadOnly?: boolean;
  initialExpanded?: boolean;
  executiveAssistants: Array<{ name: string; email: string }>;
  onSendNoteToEAs: () => void;
  meetingId: string | null;
  onCreateMeeting: (selectedSlot?: TimeSlot) => void;
  resolvedAttendeesCount: number;
  meetingSubject?: string;
  meetingDuration?: number;
  onBehalfOfLogin?: string | null;
  resolvedAttendees?: ResolvedMatch[];
  /** MeetingFormContainer rendered by the parent — displayed inside the Create Meeting modal */
  meetingFormContent?: React.ReactNode;
  /** Read-only MeetingFormContainer for the Create Meeting modal */
  meetingFormReadOnly?: React.ReactNode;
  /** Callback to expose the selected slot ID to the parent */
  onSelectedSlotChange?: (slotId: string | null) => void;
  /** Callback to expose the proposed (form) slot to the parent */
  onProposedSlotChange?: (slot: { date: string; startTime: string; endTime: string; timezone: string } | null) => void;
  /** Callback to expose the hovered slot ID to the parent */
  onHoveredSlotChange?: (slotId: string | null) => void;
  /** External time window to populate into the form (e.g. from timezone grid click) */
  populateSlot?: { date: string; startTime: string; endTime: string; timezone: string } | null;
  /** External time window to add directly as a slot (e.g. from timezone grid double-click) */
  directAddSlot?: { date: string; startTime: string; endTime: string; timezone: string } | null;
}

export const SchedulingContainer: React.FC<SchedulingContainerProps> = ({
  organizerLogin,
  currentUserAlias,
  scheduling,
  initialExpanded = true,
  executiveAssistants,
  onSendNoteToEAs,
  meetingId,
  onCreateMeeting,
  resolvedAttendeesCount,
  meetingSubject = '',
  meetingDuration = 30,
  onBehalfOfLogin,
  resolvedAttendees = [],
  meetingFormContent,
  meetingFormReadOnly,
  onSelectedSlotChange,
  onProposedSlotChange,
  onHoveredSlotChange,
  populateSlot,
  directAddSlot
}) => {
  const hasUnresolvedNames = resolvedAttendees.some((a) => !a.selectedEmployee);
  const { isCreator, isLoading, isSaving, activeTimeSlots, addTimeSlot, deleteTimeSlot, reorderTimeSlots, submitVote } = scheduling;

  const [expanded, setExpanded] = useState(initialExpanded);

  // Add time slot form state
  const [newSlotDate, setNewSlotDate] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
  const [newSlotStartTime, setNewSlotStartTime] = useState('');
  const [newSlotEndTime, setNewSlotEndTime] = useState('');
  const [newSlotTimezone, setNewSlotTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [overflowError, setOverflowError] = useState('');

  const startTimeRef = useRef(newSlotStartTime);
  startTimeRef.current = newSlotStartTime;

  // Auto-populate start time to next 15-min increment and derive end time
  useEffect(() => {
    const startTime = getNextStartTime(newSlotTimezone);
    if (!startTime) return;
    setNewSlotStartTime(startTime);
    if (meetingDuration) {
      const [hh, mm] = startTime.split(':').map(Number);
      const endMinutes = hh * 60 + mm + meetingDuration;
      if (endMinutes <= 1440) {
        setNewSlotEndTime(`${String(Math.floor((endMinutes % 1440) / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate end time when start time changes based on meeting duration
  const handleStartTimeChange = useCallback(
    (startTime: string) => {
      setNewSlotStartTime(startTime);
      setOverflowError('');
      if (startTime && meetingDuration) {
        const [hh, mm] = startTime.split(':').map(Number);
        const endMinutes = hh * 60 + mm + meetingDuration;
        if (endMinutes <= 1440) {
          const endHH = String(Math.floor((endMinutes % 1440) / 60)).padStart(2, '0');
          const endMM = String(endMinutes % 60).padStart(2, '0');
          setNewSlotEndTime(`${endHH}:${endMM}`);
        } else {
          setNewSlotEndTime('');
          setOverflowError('Meeting end time goes past midnight. Please select an earlier time or switch to the next day.');
        }
      }
    },
    [meetingDuration]
  );

  // Reset start time when date/timezone changes if current selection is now in the past
  useEffect(() => {
    const minTime = getMinStartTime(newSlotDate, newSlotTimezone);
    if (minTime && startTimeRef.current && startTimeRef.current < minTime) {
      handleStartTimeChange(minTime);
    }
  }, [newSlotDate, newSlotTimezone, handleStartTimeChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending time slots (local-only, before meeting is created)
  const [pendingTimeSlots, setPendingTimeSlots] = useState<TimeSlot[]>([]);

  // Determine if a meeting already exists (either shared or just created)
  const hasMeeting = !!scheduling.schedulingData || activeTimeSlots.length > 0;

  // Populate form fields from external source (e.g. timezone grid click)
  const populateSlotRef = useRef(populateSlot);
  useEffect(() => {
    if (populateSlot && populateSlot !== populateSlotRef.current) {
      setNewSlotDate(populateSlot.date);
      setNewSlotStartTime(populateSlot.startTime);
      setNewSlotEndTime(populateSlot.endTime);
      setNewSlotTimezone(populateSlot.timezone);
    }
    populateSlotRef.current = populateSlot;
  }, [populateSlot]);

  // Auto-add a slot from external source (e.g. timezone grid double-click) — creator only
  const directAddSlotRef = useRef(directAddSlot);
  const hasMeetingRef = useRef(hasMeeting);
  hasMeetingRef.current = hasMeeting;
  useEffect(() => {
    if (!directAddSlot || directAddSlot === directAddSlotRef.current) {
      return;
    }
    if (!isCreator) {
      return; // Don't update ref — allow retry when isCreator changes
    }
    if (hasMeetingRef.current) {
      addTimeSlot(directAddSlot.date, directAddSlot.startTime, directAddSlot.endTime, directAddSlot.timezone);
    } else {
      setPendingTimeSlots((prev) => [
        ...prev,
        {
          slotId: uuidv4(),
          date: directAddSlot.date,
          startTime: directAddSlot.startTime,
          endTime: directAddSlot.endTime,
          timezone: directAddSlot.timezone,
          order: prev.length,
          votes: []
        }
      ]);
    }
    directAddSlotRef.current = directAddSlot;
  }, [directAddSlot, addTimeSlot, isCreator]);

  const isFlushing = useRef(false);

  // ── Create Meeting modal state ─────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  // ── Delete confirmation modal state ────────────────────────────────
  const [slotToDelete, setSlotToDelete] = useState<string | null>(null);

  // ── AI Summary modal state ─────────────────────────────────────────
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryText, setSummaryText] = useState('');
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  // Load selectedSlotId from DDB when meeting loads
  useEffect(() => {
    if (!meetingId || !organizerLogin) return;
    EmployeeSettingsService.getMeeting(organizerLogin, meetingId)
      .then((meeting) => {
        if (meeting?.selectedSlotId) {
          setSelectedSlotId(meeting.selectedSlotId);
        }
      })
      .catch(() => {});
  }, [meetingId, organizerLogin]);

  // Notify parent of selected slot changes
  useEffect(() => {
    onSelectedSlotChange?.(selectedSlotId);
  }, [selectedSlotId, onSelectedSlotChange]);

  // Notify parent of proposed slot changes
  const lastProposedRef = useRef<string>('');
  useEffect(() => {
    let next: { date: string; startTime: string; endTime: string; timezone: string } | null = null;
    if (newSlotDate && newSlotStartTime && newSlotEndTime && newSlotTimezone) {
      next = { date: newSlotDate, startTime: newSlotStartTime, endTime: newSlotEndTime, timezone: newSlotTimezone };
    } else if (newSlotDate && newSlotTimezone) {
      next = { date: newSlotDate, startTime: '', endTime: '', timezone: newSlotTimezone };
    }
    const key = next ? `${next.date}|${next.startTime}|${next.endTime}|${next.timezone}` : '';
    if (key !== lastProposedRef.current) {
      lastProposedRef.current = key;
      onProposedSlotChange?.(next);
    }
  }, [newSlotDate, newSlotStartTime, newSlotEndTime, newSlotTimezone, onProposedSlotChange]);

  const timezoneOptions = useMemo(() => {
    const now = DateTime.now();
    const allZones: string[] =
      typeof (Intl as any).supportedValuesOf === 'function'
        ? ((Intl as any).supportedValuesOf('timeZone') as string[])
        : [Intl.DateTimeFormat().resolvedOptions().timeZone];
    return allZones
      .filter((tz) => IANAZone.isValidZone(tz))
      .sort((a, b) => now.setZone(a).offset - now.setZone(b).offset || a.localeCompare(b))
      .map((tz) => {
        const dt = now.setZone(tz);
        const offset = dt.toFormat('ZZ');
        const abbr = dt.toFormat('ZZZZ');
        return {
          label: abbr,
          value: tz,
          description: `(GMT${offset}) ${tz.replace(/_/g, ' ')}`
        };
      });
  }, []);

  // 12:00 AM – 11:45 PM in 15-min increments (value is 24h, label is 12h)
  const timePickerOptions = useMemo(() => {
    const opts = [];
    for (let m = 0; m < 1440; m += 15) {
      const hh24 = String(Math.floor(m / 60)).padStart(2, '0');
      const mm = String(m % 60).padStart(2, '0');
      const h12 = Math.floor(m / 60) % 12 || 12;
      const period = m < 720 ? 'AM' : 'PM';
      opts.push({ value: `${hh24}:${mm}`, label: `${h12}:${mm} ${period}` });
    }
    return opts;
  }, []);

  // Filter start time options: only show times after now when date is today
  const startTimeOptions = useMemo(() => {
    const minTime = getMinStartTime(newSlotDate, newSlotTimezone);
    if (!minTime) return timePickerOptions;
    return timePickerOptions.filter((o) => o.value >= minTime);
  }, [timePickerOptions, newSlotDate, newSlotTimezone]);

  // Filter end time options: must be after start time and after now when date is today
  const endTimeOptions = useMemo(() => {
    const minEnd = getMinEndTime(newSlotDate, newSlotTimezone, newSlotStartTime);
    const filtered = minEnd ? timePickerOptions.filter((o) => o.value > minEnd) : [...timePickerOptions];
    // Add midnight (12:00 AM next day) as the last option
    if (newSlotStartTime) {
      const withoutMidnight = filtered.filter((o) => o.value !== '00:00');
      withoutMidnight.push({ value: '00:00', label: '12:00 AM (Midnight)' });
      return withoutMidnight;
    }
    return filtered;
  }, [timePickerOptions, newSlotDate, newSlotTimezone, newSlotStartTime]);

  const startTimePastWarning = useMemo(() => {
    if (!newSlotStartTime || !newSlotDate) return '';
    const minTime = getMinStartTime(newSlotDate, newSlotTimezone);
    return minTime && newSlotStartTime < minTime ? 'Start time has already passed' : '';
  }, [newSlotDate, newSlotTimezone, newSlotStartTime]);

  const handleAddSlot = async () => {
    if (!newSlotDate || !newSlotStartTime || !newSlotEndTime || !newSlotTimezone) return;

    if (hasMeeting) {
      addTimeSlot(newSlotDate, newSlotStartTime, newSlotEndTime, newSlotTimezone);
    } else {
      const localSlot: TimeSlot = {
        slotId: uuidv4(),
        date: newSlotDate,
        startTime: newSlotStartTime,
        endTime: newSlotEndTime,
        timezone: newSlotTimezone,
        order: pendingTimeSlots.length,
        votes: []
      };
      setPendingTimeSlots((prev) => [...prev, localSlot]);
    }
    setNewSlotStartTime('');
    setNewSlotEndTime('');
  };

  // Flush pending local time slots to DDB once the meeting is created
  useEffect(() => {
    if (!scheduling.schedulingData || pendingTimeSlots.length === 0 || isFlushing.current) return;
    isFlushing.current = true;

    const flushSlots = async () => {
      for (const slot of pendingTimeSlots) {
        await addTimeSlot(slot.date, slot.startTime, slot.endTime, slot.timezone);
      }
      setPendingTimeSlots([]);
      isFlushing.current = false;
    };

    flushSlots();
  }, [scheduling.schedulingData, pendingTimeSlots, addTimeSlot]);

  // Combined time slots: DDB slots + local pending slots, selected slot sorted first
  const allTimeSlots = useMemo(() => {
    const combined = pendingTimeSlots.length === 0 ? activeTimeSlots : [...activeTimeSlots, ...pendingTimeSlots];
    if (!selectedSlotId) return combined;
    return [...combined].sort((a, b) => {
      if (a.slotId === selectedSlotId) return -1;
      if (b.slotId === selectedSlotId) return 1;
      return 0;
    });
  }, [activeTimeSlots, pendingTimeSlots, selectedSlotId]);

  // ── Memoized meeting data payload for AI summary ────────────────────
  const meetingDataPayload = useMemo(() => {
    if (allTimeSlots.length === 0) return null;

    const attendees = resolvedAttendees.map((a) => ({
      name: a.editedName || a.selectedEmployee?.employee_name || a.inputName,
      login: a.selectedEmployee?.login_name || a.inputName,
      level: a.selectedEmployee?.job_level_name ? `L${a.selectedEmployee.job_level_name}` : undefined,
      role: a.priority,
      eaName: a.selectedExecutiveAssistant?.employee_name || undefined,
      proxyFor: a.proxyForName || a.proxyForLogin || undefined
    }));

    // Map principal login → proxy login/name for vote attribution
    const proxyForMap = new Map<string, { login: string; name: string }>();
    for (const a of resolvedAttendees) {
      if (a.proxyForLogin && a.selectedEmployee?.login_name) {
        proxyForMap.set(a.proxyForLogin.toLowerCase(), {
          login: a.selectedEmployee.login_name,
          name: a.selectedEmployee.employee_name || a.selectedEmployee.login_name
        });
      }
    }

    const timeSlots = allTimeSlots.map((slot) => {
      const upVoters = slot.votes.filter((v) => v.vote === 'up').map((v) => v.loginName);
      const downVoters = slot.votes.filter((v) => v.vote === 'down').map((v) => v.loginName);
      const allVoters = new Set([...upVoters, ...downVoters]);
      const noVote = attendees
        .filter((a) => {
          if (allVoters.has(a.login)) return false;
          // If this person has a proxy who voted, they don't need to vote
          const proxy = proxyForMap.get(a.login.toLowerCase());
          if (proxy && allVoters.has(proxy.login)) return false;
          return true;
        })
        .map((a) => a.login);
      return {
        date: slot.date,
        startTime: DateTime.fromFormat(slot.startTime, 'HH:mm').toFormat('h:mm a'),
        endTime: DateTime.fromFormat(slot.endTime, 'HH:mm').toFormat('h:mm a'),
        timezone: DateTime.now().setZone(slot.timezone).toFormat('ZZZZ'),
        upVotes: upVoters,
        downVotes: downVoters,
        noVote
      };
    });

    return {
      title: meetingSubject || 'Untitled',
      duration: `${meetingDuration} minutes`,
      timezone: allTimeSlots[0]?.timezone ? DateTime.now().setZone(allTimeSlots[0].timezone).toFormat('ZZZZ') : undefined,
      organizer: organizerLogin,
      onBehalfOf: onBehalfOfLogin || undefined,
      attendees,
      timeSlots
    };
  }, [allTimeSlots, meetingSubject, meetingDuration, organizerLogin, onBehalfOfLogin, resolvedAttendees]);

  // Cache: skip Bedrock call if payload hasn't changed
  const lastPayloadRef = useRef<string | null>(null);
  const lastSummaryRef = useRef<string>('');

  const handleTimeSlotSummary = useCallback(async () => {
    if (!meetingDataPayload) return;

    setShowSummaryModal(true);
    setIsSummaryLoading(true);
    setSummaryText('');
    setSummaryError('');

    try {
      // Fetch comments for all slots in parallel and attach
      let slotsWithComments = meetingDataPayload.timeSlots;
      if (meetingId && organizerLogin) {
        const commentResults = await Promise.all(
          allTimeSlots.map(async (slot) => {
            try {
              const { SchedulingService } = await import('../../services/SchedulingService');
              const comments = await SchedulingService.getTimeSlotComments(organizerLogin, meetingId, slot.slotId);
              return { slotId: slot.slotId, comments };
            } catch {
              return { slotId: slot.slotId, comments: [] as any[] };
            }
          })
        );
        const commentsMap: Record<string, { author: string; content: string }[]> = {};
        for (const { slotId, comments } of commentResults) {
          if (comments.length > 0) {
            commentsMap[slotId] = comments.map((c) => ({ author: c.loginName, content: c.content }));
          }
        }
        slotsWithComments = meetingDataPayload.timeSlots.map((slot, i) => ({
          ...slot,
          comments: commentsMap[allTimeSlots[i].slotId] || []
        }));
      }

      const fullPayload = { ...meetingDataPayload, timeSlots: slotsWithComments };
      const fullPayloadJson = JSON.stringify(fullPayload);

      // Cache check using full payload (including comments)
      if (fullPayloadJson === lastPayloadRef.current && lastSummaryRef.current) {
        setSummaryText(lastSummaryRef.current);
        return;
      }

      const userMessage = JSON.stringify(fullPayload, null, 2);
      const result = await BedrockService.invokeWithText(userMessage, TIMESLOT_SUMMARY_PROMPT);

      lastPayloadRef.current = fullPayloadJson;
      lastSummaryRef.current = result;
      setSummaryText(result);
    } catch (error) {
      logger.error('Failed to generate time slot summary', { error });
      setSummaryError('Failed to generate summary. Please try again.');
    } finally {
      setIsSummaryLoading(false);
    }
  }, [meetingDataPayload, meetingId, organizerLogin, allTimeSlots]);

  const handleSendReminder = useCallback(() => {
    if (!summaryText) return;
    const url = MeetingService.generateReminderMailtoUrl(meetingSubject, summaryText, resolvedAttendees, meetingId, organizerLogin);
    window.open(url, '_blank');
  }, [summaryText, meetingSubject, resolvedAttendees, meetingId, organizerLogin]);

  // Local delete for pending slots
  const handleDeleteSlot = useCallback(
    (slotId: string) => {
      if (pendingTimeSlots.some((s) => s.slotId === slotId)) {
        setPendingTimeSlots((prev) => prev.filter((s) => s.slotId !== slotId));
      } else {
        setSlotToDelete(slotId);
      }
    },
    [pendingTimeSlots]
  );

  const handleConfirmDelete = useCallback(() => {
    if (slotToDelete) {
      deleteTimeSlot(slotToDelete);
      setSlotToDelete(null);
    }
  }, [slotToDelete, deleteTimeSlot]);

  const handleSortingChange = useCallback(
    ({ detail }: { detail: { items: ReadonlyArray<TimeSlot> } }) => {
      const reordered = detail.items.map((s, i) => ({ ...s, order: i }));
      reorderTimeSlots(reordered);
    },
    [reorderTimeSlots]
  );

  // ── Create Meeting modal handlers ──────────────────────────────────

  const timeSlotOptions = useMemo(
    () =>
      allTimeSlots.map((slot) => ({
        label: formatTimeSlotLabel(slot),
        value: slot.slotId
      })),
    [allTimeSlots]
  );

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateFromModal = useCallback(() => {
    const selectedSlot = allTimeSlots.find((slot) => slot.slotId === selectedSlotId);
    onCreateMeeting(selectedSlot);
    setShowCreateModal(false);
  }, [onCreateMeeting, allTimeSlots, selectedSlotId]);

  const handleDismissCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        headerText="Proposed Meeting Windows"
        headerDescription="Propose meeting times and share them with attendees for alignment."
        headerActions={
          <SpaceBetween direction="horizontal" size="xs">
            {isCreator && (
              <span className="blue-gen-ai-button">
                <Button variant="normal" iconName="gen-ai" disabled={allTimeSlots.length === 0} onClick={handleTimeSlotSummary}>
                  Summarize with AI
                </Button>
              </span>
            )}
            {isCreator && (
              <Button
                variant="normal"
                iconName="envelope"
                onClick={onSendNoteToEAs}
                disabled={resolvedAttendeesCount === 0}
                ariaLabel={`Share proposed meeting times with ${
                  executiveAssistants.length > 0
                    ? `Executive Assistants: ${executiveAssistants.map((ea) => ea.name).join(', ')}`
                    : 'attendees directly'
                }.`}
              >
                Share Proposed Times
              </Button>
            )}

            {isCreator && (
              <span className="blue-button">
                <Button
                  variant="primary"
                  onClick={handleOpenCreateModal}
                  disabled={resolvedAttendeesCount === 0 || activeTimeSlots.length === 0 || hasUnresolvedNames}
                  disabledReason={
                    activeTimeSlots.length === 0
                      ? 'Select a time slot first as you need at least one proposed time to create the meeting.'
                      : resolvedAttendeesCount === 0
                      ? 'Add at least one attendee to create a meeting'
                      : hasUnresolvedNames
                      ? "We couldn't match all attendees to known employees. Review the attendee list and correct any unrecognized names before the meeting can be created."
                      : undefined
                  }
                  iconName="calendar"
                >
                  Create Meeting
                </Button>
              </span>
            )}
          </SpaceBetween>
        }
      >
        {isLoading ? (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
            <Box>Loading scheduling data...</Box>
          </Box>
        ) : (
          <SpaceBetween direction="vertical" size="s">
            {/* Add Time Slot Form */}
            {isCreator && (
              <div className="scheduling-add-time-row">
                <FormField
                  label="Date"
                  warningText={
                    newSlotDate && [6, 7].includes(DateTime.fromFormat(newSlotDate, 'yyyy-MM-dd').weekday) // luxon weekday: 6=Saturday, 7=Sunday
                      ? `Selected date is a ${DateTime.fromFormat(newSlotDate, 'yyyy-MM-dd').toFormat('EEEE')}`
                      : ''
                  }
                >
                  <DatePicker
                    value={newSlotDate}
                    onChange={({ detail }) => setNewSlotDate(detail.value)}
                    placeholder="YYYY/MM/DD"
                    isDateEnabled={(date) => date.getTime() >= new Date(new Date().toDateString()).getTime()}
                  />
                  {newSlotDate && (
                    <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
                      {DateTime.fromFormat(newSlotDate, 'yyyy-MM-dd').toFormat('EEEE, MMMM d, yyyy')}
                    </Box>
                  )}
                </FormField>
                <FormField label="Start Time" warningText={startTimePastWarning}>
                  <Select
                    selectedOption={startTimeOptions.find((o) => o.value === newSlotStartTime) ?? null}
                    onChange={({ detail }) => handleStartTimeChange(detail.selectedOption.value ?? '')}
                    options={startTimeOptions}
                    filteringType="auto"
                    placeholder="Select start time"
                  />
                </FormField>
                <FormField
                  label="End Time"
                  errorText={
                    overflowError ||
                    (newSlotStartTime && newSlotEndTime && newSlotEndTime !== '00:00' && newSlotStartTime >= newSlotEndTime
                      ? '⚠ End time must be after start time'
                      : '')
                  }
                >
                  <Select
                    selectedOption={endTimeOptions.find((o) => o.value === newSlotEndTime) ?? null}
                    onChange={({ detail }) => {
                      setNewSlotEndTime(detail.selectedOption.value ?? '');
                      setOverflowError('');
                    }}
                    options={endTimeOptions}
                    filteringType="auto"
                    placeholder="Select end time"
                  />
                </FormField>
                <FormField label="Timezone">
                  <Select
                    selectedOption={timezoneOptions.find((o) => o.value === newSlotTimezone) ?? null}
                    onChange={({ detail }) => setNewSlotTimezone(detail.selectedOption.value ?? getUserTimezone())}
                    options={timezoneOptions}
                    filteringType="auto"
                    placeholder="Select timezone"
                  />
                </FormField>
                <div className="scheduling-add-time-btn">
                  <Button
                    variant="normal"
                    iconName="add-plus"
                    onClick={handleAddSlot}
                    loading={isSaving}
                    disabled={
                      !newSlotDate || !newSlotStartTime || !newSlotEndTime || (newSlotEndTime !== '00:00' && newSlotStartTime >= newSlotEndTime)
                    }
                  >
                    Proposed Window
                  </Button>
                </div>
              </div>
            )}

            {/* Time Slot Cards */}
            {allTimeSlots.length === 0 ? (
              <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 's' }}>
                No time slots yet. {isCreator ? 'Add a time slot above to propose meeting times.' : 'The organizer has not added time slots yet.'}
              </Box>
            ) : (
              <List
                sortable={isCreator}
                sortDisabled={!isCreator}
                items={allTimeSlots}
                renderItem={(slot: TimeSlot) => ({
                  id: slot.slotId,
                  content: (
                    <div
                      className={`time-slot-row${slot.slotId === selectedSlotId ? ' time-slot-row--selected' : ''}`}
                      onClick={
                        isCreator
                          ? () => {
                              const previousValue = selectedSlotId;
                              const newValue = selectedSlotId === slot.slotId ? null : slot.slotId;
                              setSelectedSlotId(newValue);
                              if (meetingId && organizerLogin) {
                                EmployeeSettingsService.updateMeetingMetadata(organizerLogin, meetingId, { selectedSlotId: newValue }).catch(
                                  (error) => {
                                    logger.error('Failed to persist selectedSlotId', { error, meetingId });
                                    setSelectedSlotId(previousValue);
                                  }
                                );
                              }
                            }
                          : undefined
                      }
                    >
                      {isCreator && (
                        <button
                          type="button"
                          className={`time-slot-row__pin${slot.slotId === selectedSlotId ? '' : ' time-slot-row__pin--inactive'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const previousValue = selectedSlotId;
                            const newValue = selectedSlotId === slot.slotId ? null : slot.slotId;
                            setSelectedSlotId(newValue);
                            if (meetingId && organizerLogin) {
                              EmployeeSettingsService.updateMeetingMetadata(organizerLogin, meetingId, { selectedSlotId: newValue }).catch(
                                (error) => {
                                  logger.error('Failed to persist selectedSlotId', { error, meetingId });
                                  setSelectedSlotId(previousValue);
                                }
                              );
                            }
                          }}
                          aria-label={slot.slotId === selectedSlotId ? 'Unpin this time slot' : 'Pin this time slot'}
                        >
                          <span className="time-slot-row__pin-emoji">📌</span>
                        </button>
                      )}
                      <div
                        className="time-slot-row__card"
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={() => onHoveredSlotChange?.(slot.slotId)}
                        onMouseLeave={() => onHoveredSlotChange?.(null)}
                      >
                        <TimeSlotCard
                          slot={slot}
                          currentUserAlias={currentUserAlias}
                          onVote={submitVote}
                          meetingId={meetingId}
                          creatorAlias={organizerLogin}
                          meetingSubject={meetingSubject}
                          isLocked={slot.slotId === selectedSlotId}
                        />
                      </div>
                    </div>
                  ),
                  actions: isCreator ? (
                    <Button variant="icon" iconName="remove" ariaLabel="Delete time slot" onClick={() => handleDeleteSlot(slot.slotId)} />
                  ) : undefined
                })}
                onSortingChange={handleSortingChange}
                ariaLabel="Time slots"
                disableItemPaddings={false}
                i18nStrings={{
                  liveAnnouncementDndStarted: (position: number, total: number) => `Picked up item at position ${position} of ${total}`,
                  liveAnnouncementDndDiscarded: 'Reordering cancelled',
                  liveAnnouncementDndItemReordered: (initial: number, current: number, total: number) =>
                    `Moving from ${initial} to ${current} of ${total}`,
                  liveAnnouncementDndItemCommitted: (initial: number, final: number, total: number) =>
                    `Moved from ${initial} to ${final} of ${total}`,
                  dragHandleAriaLabel: 'Drag handle',
                  dragHandleAriaDescription: 'Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to cancel'
                }}
              />
            )}
          </SpaceBetween>
        )}
      </ExpandableSection>

      {/* ── Create Meeting Modal ──────────────────────────────────── */}
      <CreateMeetingModal
        visible={showCreateModal}
        onDismiss={handleDismissCreateModal}
        onCreate={handleCreateFromModal}
        meetingFormContent={meetingFormReadOnly ?? meetingFormContent}
        timeSlotOptions={timeSlotOptions}
        selectedSlotId={selectedSlotId}
        onSelectedSlotChange={(slotId) => {
          const previousValue = selectedSlotId;
          setSelectedSlotId(slotId);
          if (meetingId && organizerLogin) {
            EmployeeSettingsService.updateMeetingMetadata(organizerLogin, meetingId, { selectedSlotId: slotId }).catch((error) => {
              logger.error('Failed to persist selectedSlotId', { error, meetingId });
              setSelectedSlotId(previousValue);
            });
          }
        }}
      />

      {/* ── Delete Time Slot Confirmation Modal ───────────────────── */}
      {slotToDelete && (
        <Modal
          visible={true}
          onDismiss={() => setSlotToDelete(null)}
          header="Delete Time Slot"
          size="medium"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setSlotToDelete(null)}>
                  Cancel
                </Button>
                <span className="blue-button">
                  <Button variant="primary" onClick={handleConfirmDelete}>
                    Delete
                  </Button>
                </span>
              </SpaceBetween>
            </Box>
          }
        >
          <Alert type="warning">
            All votes and comments associated with this time slot will be permanently deleted. Are you sure you want to proceed?
          </Alert>
        </Modal>
      )}

      {/* ── AI TimeSlot Summary Modal ─────────────────────────────── */}
      <TimeSlotSummaryModal
        visible={showSummaryModal}
        onDismiss={() => setShowSummaryModal(false)}
        isLoading={isSummaryLoading}
        summaryText={summaryText}
        error={summaryError}
        onSendReminder={handleSendReminder}
      />
    </>
  );
};
